import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Divider, message, Modal, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type PorschePaymentModuleSpecsFragment } from '../../../../api/fragments/PorschePaymentModuleSpecs';
import {
    DeletePorschePaymentSettingsDocument,
    type DeletePorschePaymentSettingsMutation,
    type DeletePorschePaymentSettingsMutationVariables,
} from '../../../../api/mutations/deletePorschePaymentSettings';

export type PorschePaymentSettingActionsProps = {
    module: PorschePaymentModuleSpecsFragment;
    paymentSetting: PorschePaymentModuleSpecsFragment['paymentSettings'][number];
};

const PorschePaymentSettingActions = ({ module, paymentSetting }: PorschePaymentSettingActionsProps) => {
    const moduleId = module.id;
    const paymentSettingId = paymentSetting.id;

    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const { t } = useTranslation(['porschePaymentModuleDetails']);

    const deleteAction = useCallback(() => {
        Modal.confirm({
            title: t('porschePaymentModuleDetails:paymentSettings.deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('porschePaymentModuleDetails:paymentSettings.deleteModal.content'),
            okText: t('porschePaymentModuleDetails:paymentSettings.deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('porschePaymentModuleDetails:paymentSettings.deleteModal.cancelButton'),
            onOk: async () => {
                message.loading({
                    content: t('porschePaymentModuleDetails:paymentSettings.deleteModal.messages.submitting'),
                    key: 'primary',
                    duration: 0,
                });

                await apolloClient
                    .mutate<DeletePorschePaymentSettingsMutation, DeletePorschePaymentSettingsMutationVariables>({
                        mutation: DeletePorschePaymentSettingsDocument,
                        variables: { moduleId, id: paymentSettingId },
                    })
                    .finally(() => {
                        message.destroy('primary');
                    });

                message.success({
                    content: t('porschePaymentModuleDetails:paymentSettings.deleteModal.messages.success'),
                    key: 'primary',
                });
            },
        });
    }, [apolloClient, moduleId, paymentSettingId, t]);

    return (
        <Space size="small" split={<Divider type="vertical" />}>
            <Button
                onClick={() => navigate(`/admin/system/modules/${moduleId}/porschePayment/${paymentSettingId}`)}
                size="small"
                style={{ padding: 0 }}
                type="link"
            >
                {t('porschePaymentModuleDetails:paymentSettingsTable.actions.update')}
            </Button>
            <Button onClick={deleteAction} size="small" style={{ padding: 0 }} type="link">
                {t('porschePaymentModuleDetails:paymentSettingsTable.actions.delete')}
            </Button>
        </Space>
    );
};

export default PorschePaymentSettingActions;
